"use client";

/**
 * Client body for the /product marketing page — the product overview the
 * nav's Product mega-menu CTA lands on.
 *
 * Structure: hero → four platform pillars → maturity ledger (shipped /
 * where-configured / preview / roadmap) → who-this-is-for-now → AI-boundaries
 * teaser (deep-links to /security#ai-boundaries) → CTA. The maturity ledger
 * mirrors docs/POSITIONING.md §2 — never promote an item here without
 * promoting it in that ledger first. All copy lives in the `productPage`
 * namespace (EN + NL in lockstep).
 */

import Link from "next/link";
import Image from "next/image";
import {
  ArrowRight,
  Bot,
  FileSearch,
  History,
  LifeBuoy,
  MessagesSquare,
  ShieldCheck,
  Users,
  Workflow,
} from "lucide-react";
import { MarketingShell } from "@/components/marketing/marketing-shell";
import {
  CtaBanner,
  FeatureGrid,
  Section,
  SectionHeader,
  UseCaseHero,
} from "@/components/marketing/marketing-kit";
import { Reveal } from "@/components/reveal";
import { VideoBlock } from "@/components/marketing/video/video-block";
import { CheckCircle2 } from "lucide-react";
import { useT } from "@/i18n";

const STATUS_GROUPS = [
  { key: "shipped", items: ["flagship", "prReview", "uiTests", "reviewOnVerdict", "replies", "replyStyle", "questions", "commandCenter", "customers", "projectRoles", "projectSetup", "llmFirst", "skills", "mailboxes", "stacks", "environmentDiscovery", "runtimeProof", "reproduction", "chatSteering", "patchQueues", "ledger", "identity", "workspaces", "lifecycle", "privacyRequests"] },
  { key: "configured", items: ["argo", "cicdGates", "fcAgent", "jsm", "jiraSync", "database", "gcp", "sandboxEnvironments", "environmentSettings", "validationStrategy", "inClusterValidation", "previews"] },
  { key: "preview", items: ["devops"] },
  { key: "roadmap", items: ["saml", "cicd", "incident"] },
] as const;

/** Real app screenshots (demo data, captured via scripts/screenshot-pages.mjs). */
const SHOTS = [
  { key: "dashboard", src: "/screenshots/home.png" },
  { key: "inbox", src: "/screenshots/inbox.png" },
  { key: "missions", src: "/screenshots/mission_control.png" },
  { key: "patches", src: "/screenshots/patches.png" },
] as const;

export function ProductContent() {
  const t = useT();
  const k = (s: string) => `productPage.${s}`;

  const feature = (ns: string, key: string, icon: React.ReactNode) => ({
    icon,
    title: t(k(`${ns}.items.${key}.title`)),
    body: t(k(`${ns}.items.${key}.body`)),
  });

  return (
    <MarketingShell>
      <UseCaseHero
        eyebrow={t(k("hero.eyebrow"))}
        title={t(k("hero.title"))}
        highlight={t(k("hero.highlight"))}
        subtitle={t(k("hero.subtitle"))}
        primary={{ href: "/demo", label: t(k("hero.primaryCta")) }}
        secondary={{ href: "/pricing", label: t(k("hero.secondaryCta")) }}
        bullets={[t(k("hero.bullet1")), t(k("hero.bullet2")), t(k("hero.bullet3"))]}
      />

      {/* ── Product video: "How FixControl works" in one minute. Poster +
            modal (never autoplay); the collapsible Dutch transcript renders
            below the player via <VideoBlock>. ─────────────────────────────── */}
      <Section id="video" muted>
        <SectionHeader
          eyebrow={t(k("video.eyebrow"))}
          title={t(k("video.title"))}
          description={t(k("video.intro"))}
        />
        <div className="mt-10">
          <VideoBlock id="fixcontrol-overview-nl" posterCaption={t(k("video.posterCaption"))} />
        </div>
        <ul className="mx-auto mt-8 flex max-w-4xl flex-wrap items-center justify-center gap-x-6 gap-y-2">
          {(["s1", "s2", "s3", "s4", "s5"] as const).map((key) => (
            <li key={key} className="inline-flex items-center gap-1.5 text-[13px] text-fg-dim">
              <CheckCircle2 size={13} className="shrink-0 text-brand-300" />
              {t(k(`video.summary.${key}`))}
            </li>
          ))}
        </ul>
      </Section>

      {/* ── Platform pillars ─────────────────────────────────────────────── */}
      <Section id="platform">
        <SectionHeader
          align="left"
          eyebrow={t(k("pillars.eyebrow"))}
          title={t(k("pillars.title"))}
          description={t(k("pillars.description"))}
        />
        <FeatureGrid
          columns={2}
          features={[
            feature("pillars", "supportIntelligence", <LifeBuoy size={18} />),
            feature("pillars", "operationalGovernance", <ShieldCheck size={18} />),
            feature("pillars", "evidenceEngine", <FileSearch size={18} />),
            feature("pillars", "missionTimeline", <History size={18} />),
            feature("pillars", "commandCenter", <MessagesSquare size={18} />),
          ]}
        />

        {/* Real product screenshots — never mocked or staged UI. */}
        <div className="mt-20 scroll-mt-24" id="screenshots">
          <SectionHeader
            align="left"
            eyebrow={t(k("shots.eyebrow"))}
            title={t(k("shots.title"))}
            description={t(k("shots.description"))}
          />
          <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2">
            {SHOTS.map((s, i) => (
              <Reveal key={s.key} delay={i * 60}>
                <figure className="overflow-hidden rounded-2xl border border-line-subtle bg-bg-raised shadow-xl">
                  <div className="flex items-center gap-2 border-b border-line-subtle px-4 py-2.5">
                    <span aria-hidden className="flex gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-full bg-line-subtle" />
                      <span className="h-2.5 w-2.5 rounded-full bg-line-subtle" />
                      <span className="h-2.5 w-2.5 rounded-full bg-line-subtle" />
                    </span>
                    <span className="ml-1 text-[11px] font-mono uppercase tracking-[0.18em] text-fg-muted">
                      {t(k(`shots.items.${s.key}.label`))}
                    </span>
                  </div>
                  <div className="relative aspect-[16/10] bg-bg-base">
                    <Image
                      src={s.src}
                      alt={t(k(`shots.items.${s.key}.alt`))}
                      fill
                      className="object-cover object-top"
                      sizes="(max-width: 768px) 100vw, 560px"
                    />
                  </div>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* ── Maturity ledger — shipped / configured / preview / roadmap ───── */}
      <Section id="status" muted>
        <SectionHeader
          align="left"
          eyebrow={t(k("status.eyebrow"))}
          title={t(k("status.title"))}
          description={t(k("status.description"))}
        />
        <div className="grid gap-4 md:grid-cols-2">
          {STATUS_GROUPS.map((group, gi) => (
            <Reveal key={group.key} delay={gi * 60}>
              <div className="card h-full p-5 sm:p-6">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full border border-line-subtle bg-bg-elev px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-fg-muted">
                    {t(k(`status.${group.key}.tag`))}
                  </span>
                  <h3 className="text-[15px] font-semibold text-fg-bright leading-snug">
                    {t(k(`status.${group.key}.title`))}
                  </h3>
                </div>
                <ul className="mt-3.5 space-y-2.5">
                  {group.items.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-[13.5px] text-fg-dim leading-relaxed">
                      <span aria-hidden className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-fg-muted" />
                      {t(k(`status.${group.key}.items.${item}`))}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
        <Link
          href="/product/change-validation"
          className="mt-6 inline-flex items-center gap-1.5 text-[14px] font-medium text-brand-300 hover:text-brand-200 transition"
        >
          {t(k("status.link"))}
          <ArrowRight size={15} aria-hidden />
        </Link>
      </Section>

      {/* ── Who this is for now ──────────────────────────────────────────── */}
      <Section id="fit">
        <SectionHeader
          align="left"
          eyebrow={t(k("fit.eyebrow"))}
          title={t(k("fit.title"))}
          description={t(k("fit.description"))}
        />
        <FeatureGrid
          features={[
            feature("fit", "teams", <Workflow size={18} />),
            feature("fit", "pilot", <Users size={18} />),
            feature("fit", "notReplacement", <Bot size={18} />),
          ]}
        />
      </Section>

      {/* ── AI boundaries teaser → /security#ai-boundaries ───────────────── */}
      <Section id="boundaries" muted>
        <SectionHeader
          align="left"
          eyebrow={t(k("boundaries.eyebrow"))}
          title={t(k("boundaries.title"))}
          description={t(k("boundaries.description"))}
        />
        <Link
          href="/security#ai-boundaries"
          className="inline-flex items-center gap-1.5 text-[14px] font-medium text-brand-300 hover:text-brand-200 transition"
        >
          {t(k("boundaries.link"))}
          <ArrowRight size={15} aria-hidden />
        </Link>
      </Section>

      <CtaBanner
        title={t(k("cta.title"))}
        description={t(k("cta.description"))}
        primary={{ href: "/demo", label: t(k("cta.primary")) }}
        secondary={{ href: "/pricing", label: t(k("cta.secondary")) }}
      />
    </MarketingShell>
  );
}
